/**
 * Geometry for the skill-fit donut. Everything here is a pure function of the `size` prop and
 * nothing measures the DOM, so the chart renders identically in jsdom and the browser, and the
 * physics sim and the static render share one source of truth for marble positions.
 *
 * Angle convention matches journeysMapUtils: 0deg at 12 o'clock, increasing clockwise, in
 * SVG screen coordinates (+y down).
 */

export const DEG_TO_RAD = Math.PI / 180;

export interface Point {
    x: number;
    y: number;
}

export interface SegmentAngles {
    startDeg: number;
    endDeg: number;
}

export type SkillFitSegment = 'need' | 'have';

const clamp = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high);

const round = (value: number) => Math.round(value * 100) / 100;

export const polarToCartesian = (cx: number, cy: number, radius: number, deg: number): Point => {
    const rad = (deg - 90) * DEG_TO_RAD;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
};

/** Radii as fractions of `size`, so the chart scales from one number. */
export const DONUT_RATIOS = {
    outerRadius: 130 / 280,
    innerRadius: 97 / 280,
    strokeWidth: 2 / 280,
    /**
     * Clearance between a marble's edge and the band edge. Enough that marbles read as sitting
     * inside the arc rather than tangent to its outline. Any tighter and they looked like they
     * were spilling out of the fill.
     */
    bandPadding: 4 / 280,
} as const;

export interface DonutMetrics {
    size: number;
    cx: number;
    cy: number;
    rOuter: number;
    rInner: number;
    rMid: number;
    band: number;
    strokeWidth: number;
    bandPadding: number;
}

export const donutMetrics = (size: number): DonutMetrics => {
    const safeSize = size > 0 ? size : 0;
    const rOuter = safeSize * DONUT_RATIOS.outerRadius;
    const rInner = safeSize * DONUT_RATIOS.innerRadius;

    return {
        size: safeSize,
        cx: safeSize / 2,
        cy: safeSize / 2,
        rOuter,
        rInner,
        rMid: (rOuter + rInner) / 2,
        band: rOuter - rInner,
        strokeWidth: Math.max(1, safeSize * DONUT_RATIOS.strokeWidth),
        bandPadding: safeSize * DONUT_RATIOS.bandPadding,
    };
};

/**
 * A full ring. A single arc command cannot span 360deg, and splitting into two 180deg sectors
 * leaves an antialiasing seam, so draw outer clockwise and inner counter-clockwise and render
 * with `fillRule="evenodd"`.
 */
export const fullAnnulusPath = (metrics: DonutMetrics): string => {
    const { cx, cy, rOuter, rInner } = metrics;

    return [
        `M ${round(cx - rOuter)} ${round(cy)}`,
        `A ${round(rOuter)} ${round(rOuter)} 0 1 0 ${round(cx + rOuter)} ${round(cy)}`,
        `A ${round(rOuter)} ${round(rOuter)} 0 1 0 ${round(cx - rOuter)} ${round(cy)}`,
        'Z',
        `M ${round(cx - rInner)} ${round(cy)}`,
        `A ${round(rInner)} ${round(rInner)} 0 1 1 ${round(cx + rInner)} ${round(cy)}`,
        `A ${round(rInner)} ${round(rInner)} 0 1 1 ${round(cx - rInner)} ${round(cy)}`,
        'Z',
    ].join(' ');
};

/**
 * Annular sector with semicircular end caps, so both ends of the arc read as rounded.
 * The caps are inset by their own angular width, keeping the arc inside [startDeg, endDeg].
 *
 * Built as a closed fillable path rather than a round-linecap stroke, because the design gives
 * the arc both a fill and an outline, and a stroked path could only express one of the two.
 */
export const donutRoundedSectorPath = (
    metrics: DonutMetrics,
    startDeg: number,
    endDeg: number,
): string => {
    const sweep = endDeg - startDeg;

    if (!Number.isFinite(sweep) || sweep <= 0.01 || metrics.size <= 0) {
        return '';
    }
    if (sweep >= 359.99) {
        return fullAnnulusPath(metrics);
    }

    const { cx, cy, rOuter, rInner, rMid, band } = metrics;
    const capRadius = band / 2;
    const capDeg = Math.asin(clamp(capRadius / rMid, 0, 1)) / DEG_TO_RAD;

    // Too short to hold both caps, so collapse to a single round dot on the centreline.
    if (sweep <= capDeg * 2) {
        const centre = polarToCartesian(cx, cy, rMid, (startDeg + endDeg) / 2);
        return [
            `M ${round(centre.x - capRadius)} ${round(centre.y)}`,
            `A ${round(capRadius)} ${round(capRadius)} 0 1 0 ${round(centre.x + capRadius)} ${round(centre.y)}`,
            `A ${round(capRadius)} ${round(capRadius)} 0 1 0 ${round(centre.x - capRadius)} ${round(centre.y)}`,
            'Z',
        ].join(' ');
    }

    const from = startDeg + capDeg;
    const to = endDeg - capDeg;
    const largeArc = to - from > 180 ? 1 : 0;
    const outerStart = polarToCartesian(cx, cy, rOuter, from);
    const outerEnd = polarToCartesian(cx, cy, rOuter, to);
    const innerEnd = polarToCartesian(cx, cy, rInner, to);
    const innerStart = polarToCartesian(cx, cy, rInner, from);

    return [
        `M ${round(outerStart.x)} ${round(outerStart.y)}`,
        `A ${round(rOuter)} ${round(rOuter)} 0 ${largeArc} 1 ${round(outerEnd.x)} ${round(outerEnd.y)}`,
        // Trailing cap, bulging forward past `to`.
        `A ${round(capRadius)} ${round(capRadius)} 0 0 1 ${round(innerEnd.x)} ${round(innerEnd.y)}`,
        `A ${round(rInner)} ${round(rInner)} 0 ${largeArc} 0 ${round(innerStart.x)} ${round(innerStart.y)}`,
        // Leading cap, bulging back past `from`.
        `A ${round(capRadius)} ${round(capRadius)} 0 0 1 ${round(outerStart.x)} ${round(outerStart.y)}`,
        'Z',
    ].join(' ');
};

/**
 * The representative of `deg` closest to `referenceDeg`, offset by some whole number of turns.
 * Angles are cyclic but a segment's range is not, so 359deg and -1deg point the same way yet
 * only one compares sensibly against a segment spanning [0, 205].
 */
export const wrapAngleNear = (deg: number, referenceDeg: number): number => {
    const difference = ((((deg - referenceDeg + 180) % 360) + 360) % 360) - 180;

    return referenceDeg + difference;
};

/** Angular half-width of a rounded end cap, measured on the band centreline. */
export const capInsetDeg = (metrics: DonutMetrics): number =>
    Math.asin(clamp((metrics.band / 2) / metrics.rMid, 0, 1)) / DEG_TO_RAD;

/**
 * What each segment covers: The sweep it is painted as, and so also the stretch its marbles may occupy.
 * Read the way donutRoundedSectorPath reads its arguments, a cap inside each end, which is what keeps the pile inside the shape it can see.
 *
 * The selected arc is painted over the other, its caps hanging a cap past each join, and the one underneath loses exactly that stretch.
 * Changing the selection therefore hands one arc the overlapped tips and takes them off the other.
 */
export const marbleSegmentAngles = (
    metrics: DonutMetrics,
    angles: Record<SkillFitSegment, SegmentAngles>,
    selected: SkillFitSegment,
): Record<SkillFitSegment, SegmentAngles> => {
    const capDeg = capInsetDeg(metrics);

    const bounds = ({ startDeg, endDeg }: SegmentAngles, isSelected: boolean): SegmentAngles => {
        // Nothing to hold: no marbles belong to a segment with no sweep.
        if (endDeg - startDeg <= 0) {
            return { startDeg, endDeg };
        }
        // A segment that fills the ring has neither caps nor a join to keep clear of.
        if (endDeg - startDeg >= 359.99) {
            return { startDeg, endDeg };
        }
        if (isSelected) {
            return { startDeg: startDeg - capDeg, endDeg: endDeg + capDeg };
        }
        // Shorter than the caps it would keep clear of, so the marbles pile at its middle.
        if (endDeg - startDeg <= capDeg * 2) {
            const middle = (startDeg + endDeg) / 2;
            return { startDeg: middle, endDeg: middle };
        }
        return { startDeg: startDeg + capDeg, endDeg: endDeg - capDeg };
    };

    return {
        have: bounds(angles.have, selected === 'have'),
        need: bounds(angles.need, selected === 'need'),
    };
};

/**
 * What the segments cover partway through a selection change, `progress` of the way from the arrangement `from` was selected
 * in to the one `selected` is. Both the arcs and the marble bounds come off this, so the arc growing over the join and the
 * pile rolling out of its way move together, and neither is ever painted over the other.
 */
export const shiftingSegmentAngles = (
    metrics: DonutMetrics,
    angles: Record<SkillFitSegment, SegmentAngles>,
    selected: SkillFitSegment,
    from: SkillFitSegment,
    progress: number,
): Record<SkillFitSegment, SegmentAngles> => {
    const to = marbleSegmentAngles(metrics, angles, selected);

    if (from === selected || progress >= 1) {
        return to;
    }
    const at = clamp(progress, 0, 1);
    const was = marbleSegmentAngles(metrics, angles, from);
    const between = (a: SegmentAngles, b: SegmentAngles): SegmentAngles => ({
        startDeg: a.startDeg + (b.startDeg - a.startDeg) * at,
        endDeg: a.endDeg + (b.endDeg - a.endDeg) * at,
    });

    return { have: between(was.have, to.have), need: between(was.need, to.need) };
};

/** How far outside the arc the focus ring is drawn, and how thick it is. */
export const FOCUS_RING_OFFSET = 4;
export const FOCUS_RING_STROKE = 3;

/** Metrics for the focus ring, an outward-offset copy of the band. */
export const focusRingMetrics = (metrics: DonutMetrics): DonutMetrics => ({
    ...metrics,
    rOuter: metrics.rOuter + FOCUS_RING_OFFSET,
    rInner: Math.max(0, metrics.rInner - FOCUS_RING_OFFSET),
});

/** The have arc starts at the top and runs clockwise. Need is the remainder. */
export const segmentAngles = (
    matchedCount: number,
    totalCount: number,
): Record<SkillFitSegment, SegmentAngles> => {
    const safeTotal = Number.isFinite(totalCount) && totalCount > 0 ? totalCount : 0;
    const safeMatched = safeTotal === 0 ? 0 : clamp(matchedCount, 0, safeTotal);
    const haveSweep = safeTotal === 0 ? 0 : (safeMatched / safeTotal) * 360;

    return {
        have: { startDeg: 0, endDeg: haveSweep },
        need: { startDeg: haveSweep, endDeg: 360 },
    };
};

/** Small, so marbles nestle against each other rather than sitting in a spaced-out row. */
export const MARBLE_GAP_RATIO = 0.1;
/** Floor for the gap once marbles would otherwise drop below the minimum diameter. */
export const MARBLE_MIN_GAP_RATIO = 0.04;
export const MARBLE_MIN_DIAMETER_RATIO = 6 / 280;
export const MARBLE_MAX_ROWS = 2;
/**
 * Small enough that two fit side by side inside a rounded end cap, which is where the design
 * shows them nestling. Bigger and a pile could only ever be one marble deep. Much smaller and
 * the pile leaves slack against the band walls.
 */
export const MARBLE_MAX_BAND_FRACTION = 0.38;
/** Centre-to-centre spacing between nested rows, as a multiple of the marble diameter. */
export const ROW_SPACING_RATIO = 1.05;

/**
 * Largest marble diameter such that `count` of them fit inside `sweepDeg` at `radius`.
 * Uses the chord rather than the arc length, which lets neighbours overlap slightly.
 */
export const arcFitDiameter = (
    radius: number,
    sweepDeg: number,
    count: number,
    gapRatio = MARBLE_GAP_RATIO,
): number => {
    if (count <= 0 || sweepDeg <= 0 || radius <= 0) {
        return 0;
    }
    // The chord peaks at a half-step of 90deg, past which the formula turns back down and
    // would spuriously shrink a one- or two-marble ring to nothing. Clamping lets the
    // tangential constraint stop binding, leaving the band width in charge.
    const halfStepRad = Math.min((sweepDeg * DEG_TO_RAD) / (2 * count), Math.PI / 2);

    return (2 * radius * Math.sin(halfStepRad)) / (1 + gapRatio);
};

/**
 * Because each segment's sweep is proportional to its own count, this is the same value for
 * both segments, so marbles never resize when the selection or the have/need split changes,
 * only when `totalCount` does.
 */
export const marbleDiameter = (
    metrics: DonutMetrics,
    totalCount: number,
    rows = 1,
    gapRatio = MARBLE_GAP_RATIO,
): number => {
    if (totalCount <= 0 || rows <= 0 || metrics.size <= 0) {
        return 0;
    }
    const radialLimit = Math.min(
        (metrics.band - 2 * metrics.bandPadding) / rows,
        metrics.band * MARBLE_MAX_BAND_FRACTION,
    );
    // Size against the innermost row, which has the least circumference. Measuring at the
    // centreline overestimates how many fit once there is more than one row.
    const rowOffset = ((rows - 1) / 2) * radialLimit * ROW_SPACING_RATIO;
    const innerRowRadius = Math.max(
        metrics.rInner + metrics.bandPadding,
        metrics.rMid - rowOffset,
    );
    const tangentialLimit = arcFitDiameter(innerRowRadius, 360, totalCount / rows, gapRatio);

    return Math.max(0, Math.min(radialLimit, tangentialLimit));
};

export interface MarbleSizing {
    rows: number;
    diameter: number;
    gapRatio: number;
}

/**
 * Escalation ladder for large skill counts. Shrink within one row, then add a second row, then
 * tighten the gap. "One skill = one marble" is the metaphor, so marbles are never capped or
 * aggregated. Callers with very high counts pass a larger `size`.
 */
export const resolveMarbleSizing = (metrics: DonutMetrics, totalCount: number): MarbleSizing => {
    const floor = metrics.size * MARBLE_MIN_DIAMETER_RATIO;

    for (let rows = 1; rows <= MARBLE_MAX_ROWS; rows += 1) {
        const diameter = marbleDiameter(metrics, totalCount, rows);
        if (diameter >= floor) {
            return { rows, diameter, gapRatio: MARBLE_GAP_RATIO };
        }
    }

    return {
        rows: MARBLE_MAX_ROWS,
        diameter: marbleDiameter(metrics, totalCount, MARBLE_MAX_ROWS, MARBLE_MIN_GAP_RATIO),
        gapRatio: MARBLE_MIN_GAP_RATIO,
    };
};

export interface MarblePosition {
    x: number;
    y: number;
    angleDeg: number;
    radius: number;
    row: number;
}

export interface MarbleLayout {
    diameter: number;
    rows: number;
    positions: MarblePosition[];
}

/** The lowest point of the ring in this angle convention, where gravity piles marbles. */
export const GRAVITY_LOW_DEG = 180;

/**
 * Rest positions for one segment's marbles, the analytic solution the physics sim converges to
 * under downward gravity, which is why the static render and the animated render agree.
 */
export const layoutSegmentMarbles = (
    metrics: DonutMetrics,
    segment: SegmentAngles,
    count: number,
    diameter: number,
    rows = 1,
    gapRatio = MARBLE_GAP_RATIO,
): MarbleLayout => {
    if (count <= 0 || diameter <= 0 || rows <= 0) {
        return { diameter: 0, rows: 1, positions: [] };
    }

    const positions: MarblePosition[] = [];
    const perRow = Math.ceil(count / rows);
    const marbleRadius = diameter / 2;

    for (let row = 0; row < rows; row += 1) {
        const rowCount = Math.min(perRow, count - row * perRow);
        if (rowCount <= 0) {
            break;
        }

        // Rows straddle the band centreline, so a single row sits centred in the band.
        // Each marble then rides its row like a bead on a wire, the sim only moving them along
        // the arc, which keeps them visually centred instead of pressed to the outer wall.
        const rowRadius = metrics.rMid + (row - (rows - 1) / 2) * diameter * ROW_SPACING_RATIO;

        // Derived per row: the same angular step spans a shorter chord at a smaller radius.
        const idealStepDeg = (2 * Math.asin(clamp((diameter * (1 + gapRatio)) / (2 * rowRadius), 0, 1)))
            / DEG_TO_RAD;
        // Inset by half a marble's angular width so the shape, not its centre, stays inside.
        const halfMarbleDeg = Math.asin(clamp(marbleRadius / rowRadius, 0, 1)) / DEG_TO_RAD;
        const low = segment.startDeg + halfMarbleDeg;
        const high = segment.endDeg - halfMarbleDeg;
        const available = Math.max(0, high - low);
        // Last-resort compression, since containment wins over spacing if sizing was optimistic.
        const stepDeg = rowCount > 1 && (rowCount - 1) * idealStepDeg > available
            ? available / (rowCount - 1)
            : idealStepDeg;
        const runSpan = (rowCount - 1) * stepDeg;
        const gravityLow = clamp(GRAVITY_LOW_DEG, segment.startDeg, segment.endDeg);
        const first = clamp(gravityLow - runSpan / 2, low, Math.max(low, high - runSpan));

        for (let index = 0; index < rowCount; index += 1) {
            const angleDeg = first + index * stepDeg;
            const { x, y } = polarToCartesian(metrics.cx, metrics.cy, rowRadius, angleDeg);
            positions.push({ x, y, angleDeg, radius: rowRadius, row });
        }
    }

    return { diameter, rows, positions };
};

/**
 * The stretch of a segment the marbles are seeded into, centred on where gravity collects
 * them. Deliberately about half the arc a single-file run would need, so the solver stacks them
 * into a heap rather than settling them into a tidy line along the wall, which is what real
 * marbles do when they all arrive at the same low point.
 */
export const seedWindowForSegment = (
    metrics: DonutMetrics,
    segment: SegmentAngles,
    count: number,
    diameter: number,
    gapRatio = MARBLE_GAP_RATIO,
): SegmentAngles => {
    if (count <= 1) {
        return segment;
    }

    const stepDeg = (2 * Math.asin(clamp((diameter * (1 + gapRatio)) / (2 * metrics.rMid), 0, 1)))
        / DEG_TO_RAD;
    const halfSpan = ((count - 1) * stepDeg) / 4;
    const gravityLow = clamp(GRAVITY_LOW_DEG, segment.startDeg, segment.endDeg);

    return {
        startDeg: Math.max(segment.startDeg, gravityLow - halfSpan),
        endDeg: Math.min(segment.endDeg, gravityLow + halfSpan),
    };
};

/** Equilateral triangle inscribed in a circle of `diameter`, apex up at a rotation of 0. */
export const trianglePoints = (diameter: number): string => {
    const radius = diameter / 2;

    return [0, 120, 240]
        .map(angle => polarToCartesian(0, 0, radius, angle))
        .map(({ x, y }) => `${round(x)},${round(y)}`)
        .join(' ');
};

export const buildSkillFitDonutLabel = (
    fitPercentage: number,
    matchedCount: number,
    totalCount: number,
): string => `Circular progress chart of skill match percentage, showing ${fitPercentage}% fit `
    + `with ${matchedCount} of ${totalCount} required skills met.`;

export const buildSkillFitCountLabel = (matchedCount: number, totalCount: number): string =>
    `${matchedCount} skill${matchedCount === 1 ? '' : 's'} out of ${totalCount}`;
